import { useEffect, useState } from "react";
import ScoreBadge from "@/components/feature/ScoreBadge";
import StatusBadge from "@/components/feature/StatusBadge";
import { formatFollowers, type Prospect } from "@/pages/prospectar/components/ProspectCard";

type ProspectModalProps = {
  prospect: Prospect | null;
  opener: string | null;
  approved: boolean;
  sent: boolean;
  generating: boolean;
  onClose: () => void;
  onGenerate: (p: Prospect) => void;
  onApprove: (p: Prospect) => void;
  onSend: (p: Prospect) => void;
};

export default function ProspectModal({
  prospect,
  opener,
  approved,
  sent,
  generating,
  onClose,
  onGenerate,
  onApprove,
  onSend,
}: ProspectModalProps) {
  const [tab, setTab] = useState<"perfil" | "opener">("perfil");

  useEffect(() => {
    if (prospect) {
      setTab("perfil");
    }
  }, [prospect]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!prospect) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 vv-safe-x">
      <div className="fixed inset-0 bg-background-950/70 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative w-full sm:max-w-lg bg-background-50 rounded-t-3xl sm:rounded-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-background-950 text-white p-5 rounded-t-3xl sm:rounded-t-2xl overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-60"
            style={{ background: "radial-gradient(420px 180px at 90% 0%, oklch(var(--accent-700) / 0.5), transparent 60%)" }}
          ></div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="relative flex items-center gap-4">
            <img
              src={prospect.photo}
              alt={`Foto de ${prospect.name}`}
              className="w-16 h-16 rounded-2xl object-cover object-top border-2 border-primary-500"
            />
            <div className="min-w-0">
              <h2 className="font-heading font-extrabold text-xl truncate">{prospect.name}</h2>
              <div className="text-sm text-foreground-300">{prospect.username} · {prospect.platform}</div>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <span className="inline-flex items-center gap-1 text-[11px] bg-white/10 px-2 py-0.5 rounded-full">
                  <i className="ri-group-line"></i> {formatFollowers(prospect.followers)} seguidores
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] bg-white/10 px-2 py-0.5 rounded-full">
                  <i className="ri-price-tag-3-line"></i> {prospect.niche}
                </span>
              </div>
            </div>
            <div className="ml-auto hidden sm:block">
              <ScoreBadge score={prospect.score} size="md" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-4">
          <div className="inline-flex items-center gap-1 bg-background-100 rounded-full p-1">
            <button
              onClick={() => setTab("perfil")}
              className={[
                "px-4 py-1.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors",
                tab === "perfil" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900",
              ].join(" ")}
            >
              Perfil
            </button>
            <button
              onClick={() => setTab("opener")}
              className={[
                "px-4 py-1.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors",
                tab === "opener" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900",
              ].join(" ")}
            >
              Opener IA
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5">
          {tab === "perfil" ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wide">Estado</span>
                <StatusBadge status={prospect.status} size="md" />
              </div>
              <div className="rounded-2xl bg-accent-500/10 border border-accent-500/25 p-4">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 mb-1">
                  <i className="ri-sparkling-2-fill"></i> Por qué te lo recomendamos
                </div>
                <p className="text-sm text-foreground-800">{prospect.reason}</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-background-100 border border-background-200 rounded-xl p-3 text-center">
                  <div className="font-heading font-extrabold text-lg text-primary-600">{prospect.score}</div>
                  <div className="text-[11px] text-foreground-500">Score IA</div>
                </div>
                <div className="bg-background-100 border border-background-200 rounded-xl p-3 text-center">
                  <div className="font-heading font-extrabold text-lg text-accent-600">{formatFollowers(prospect.followers)}</div>
                  <div className="text-[11px] text-foreground-500">Seguidores</div>
                </div>
                <div className="bg-background-100 border border-background-200 rounded-xl p-3 text-center">
                  <div className="font-heading font-bold text-sm text-foreground-950 mt-1">{prospect.niche}</div>
                  <div className="text-[11px] text-foreground-500 mt-1">Nicho</div>
                </div>
              </div>
              <div className="rounded-2xl bg-background-100 border border-background-200 p-4 flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-primary-500/15 text-primary-600 flex items-center justify-center">
                  <i className="ri-focus-3-line"></i>
                </span>
                <div>
                  <div className="text-[11px] text-foreground-500">Próxima acción sugerida</div>
                  <div className="text-sm font-semibold text-foreground-950">{prospect.nextAction} · {prospect.followUp}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {generating ? (
                <div className="rounded-2xl bg-background-100 border border-background-200 p-5 flex items-center gap-3">
                  <i className="ri-loader-4-line animate-spin text-accent-600 text-xl"></i>
                  <span className="text-sm text-foreground-700">Tu Guía IA está escribiendo un opener personalizado…</span>
                </div>
              ) : opener ? (
                <div className="rounded-2xl bg-background-100 border border-background-200 p-4">
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 mb-2">
                    <i className="ri-magic-line"></i> Opener generado por IA
                  </div>
                  <p className="text-sm text-foreground-800 whitespace-pre-line">{opener}</p>
                </div>
              ) : (
                <div className="rounded-2xl bg-background-100 border border-dashed border-background-300 p-5 text-center">
                  <i className="ri-magic-line text-2xl text-accent-500"></i>
                  <p className="text-sm text-foreground-600 mt-2">
                    Aún no has generado un opener para {prospect.name}. Deja que la IA escriba el primer mensaje perfecto.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => onGenerate(prospect)}
            disabled={generating}
            className="flex-1 py-3 rounded-xl bg-accent-500 text-white font-heading font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap hover:bg-accent-600 disabled:opacity-60 transition-colors"
          >
            <i className="ri-magic-line"></i> {opener ? "Mejorar opener" : "Generar opener"}
          </button>
          <button
            onClick={() => onApprove(prospect)}
            disabled={approved || !opener}
            className={[
              "flex-1 py-3 rounded-xl font-heading font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50",
              approved ? "bg-primary-500 text-background-950" : "bg-primary-500/10 text-primary-700 border border-primary-500/30 hover:bg-primary-500/20",
            ].join(" ")}
          >
            <i className={approved ? "ri-check-double-line" : "ri-check-line"}></i>
            {approved ? "Aprobado" : "Aprobar"}
          </button>
          <button
            onClick={() => onSend(prospect)}
            disabled={sent || !approved}
            className={[
              "flex-1 py-3 rounded-xl font-heading font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50",
              sent ? "bg-secondary-500 text-white" : "bg-background-950 text-white hover:bg-background-900",
            ].join(" ")}
          >
            <i className={sent ? "ri-send-plane-fill" : "ri-send-plane-line"}></i>
            {sent ? "Enviado" : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}
